import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Send } from 'lucide-react';
import { submitForm, fetchFormStatus, fetchFormSubmission } from '../../../services/employeeFormsApi';
import { formatDate } from '../../../utils/dateUtils';

export default function PFNominationPreview() {
  const navigate = useNavigate();
  const { formCode } = useParams();
  const [formData, setFormData] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [formStatus, setFormStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadFormData = async () => {
      try {
        const code = formCode || 'PF_NOM';
        setLoading(true);
        setError(null);

        // Load form submission from backend
        const submission = await fetchFormSubmission(code);
        
        if (submission && submission.form_data) {
          setFormData(submission.form_data);
          setFormStatus({
            status: submission.status,
            submitted_at: submission.submitted_at,
            updated_at: submission.updated_at
          });
        } else {
          setError('No draft or submitted form found.');
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Failed to load form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [formCode]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const code = formCode || 'PF_NOM';
      await submitForm(code, formData);
      alert('Form submitted successfully!');
      navigate('/user-dashboard?tab=my-forms', { replace: true });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = () => {
    if (!formStatus) return true;
    return formStatus.status === 'DRAFT' || formStatus.status === 'REJECTED';
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': 'Draft',
      'SUBMITTED': 'Submitted',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'DRAFT': 'bg-orange-100 text-orange-700 border border-orange-200',
      'SUBMITTED': 'bg-blue-100 text-blue-700 border border-blue-200',
      'APPROVED': 'bg-green-100 text-green-700 border border-green-200',
      'REJECTED': 'bg-red-100 text-red-700 border border-red-200'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-700 border border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error || !formData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{error || 'No form data available'}</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Forms
          </button>
        </div>
      </div>
    );
  }

  const currentDate = formatDate(new Date());

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Status Badge */}
        {formStatus && (
          <div className="mb-4 flex items-center justify-between">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(formStatus.status)}`}
            >
              {getStatusDisplay(formStatus.status)}
            </span>
            {formStatus.submitted_at && (
              <span className="text-sm text-gray-500">
                Submitted: {formatDate(formStatus.submitted_at)}
              </span>
            )}
          </div>
        )}

        {/* Document Container */}
        <div className="bg-white border border-gray-300 shadow-sm" style={{ minHeight: '1123px' }}>
          {/* Header */}
          <div className="text-center border-b-2 border-black py-6 px-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Pune Cantonment Board</h1>
            <p className="text-sm text-gray-700 mb-4">Golibar Maidan, Pune-1</p>
            <div className="inline-block border-2 border-black px-6 py-1 mb-2">
              <span className="font-bold text-lg">Form "A"</span>
            </div>
            <p className="text-sm font-semibold text-gray-800 mt-3">SCHEDULE II</p>
            <p className="text-sm text-gray-700 mt-1">Form of Nomination when depositor has a dependent or dependants.</p>
          </div>

          {/* Declaration Section */}
          <div className="px-8 py-6 border-b border-gray-300">
            <p className="text-sm text-gray-800 leading-relaxed text-justify">
              &nbsp;&nbsp;&nbsp;&nbsp; I hereby direct that the amount at my credit in the Provident Fund, in the event of my death, 
              shall be paid to the nominee(s) mentioned below in the proportion shown against their names. 
              I hereby certify that the person(s) mentioned below is/are dependent on me and that I have not 
              made any other nomination for the Provident Fund.
            </p>
          </div>

          {/* Depositor Information */}
          <div className="px-8 py-4 border-b border-gray-300">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-semibold text-sm text-gray-700">Name of Depositor:</span>
                <p className="text-sm text-gray-900 mt-1">{formData.depositorName || '________________________'}</p>
              </div>
              <div>
                <span className="font-semibold text-sm text-gray-700">Address of Depositor:</span>
                <p className="text-sm text-gray-900 mt-1 whitespace-pre-line">{formData.depositorAddress || '________________________'}</p>
              </div>
            </div>
          </div>

          {/* Nominee Table */}
          <div className="px-8 py-6 border-b border-gray-300">
            <table className="w-full border-collapse border border-gray-400">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Name and Address of the Nominee or Nominees
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Relationship if any with the Depositor
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Age of the Nominee
                  </th>
                  <th className="border border-gray-400 px-3 py-2 text-left text-xs font-semibold text-gray-800">
                    Amount of Share of Accumulations
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.nominees && formData.nominees.length > 0 ? (
                  formData.nominees.map((nominee, index) => (
                    <tr key={index}>
                      <td className="border border-gray-400 px-3 py-2 align-top">
                        <div className="text-sm text-gray-900">
                          <p className="font-medium">{nominee.name || '________________________'}</p>
                          <p className="mt-1 whitespace-pre-line text-xs">{nominee.address || '________________________'}</p>
                        </div>
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.relationship || '________________________'}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.age || '____'}
                      </td>
                      <td className="border border-gray-400 px-3 py-2 text-sm text-gray-900 align-top">
                        {nominee.sharePercentage ? `${nominee.sharePercentage}%` : '____%'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="border border-gray-400 px-3 py-2 text-sm text-gray-400" colSpan="4">
                      No nominees added
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Signature Section */}
          <div className="px-8 py-6">
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <span className="text-sm text-gray-700">Date:</span>
                <div className="mt-2 border-b border-gray-400 pb-1">
                  <span className="text-sm text-gray-900">{currentDate}</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-700">Signature of Depositor</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[200px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
            </div>

            {/* Witnesses Section */}
            <div className="mb-8">
              <p className="text-sm font-semibold text-gray-800 mb-4">Two Witnesses to Signature</p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <span className="text-sm text-gray-700">1.</span>
                  <div className="mt-2 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                  <div className="mt-4 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-700">2.</span>
                  <div className="mt-2 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                  <div className="mt-4 border-b border-gray-400 pb-1">
                    <span className="text-sm text-gray-400">________________________</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chief Executive Officer Section */}
            <div className="text-right mt-12">
              <p className="text-sm text-gray-700">Chief Executive Officer</p>
              <p className="text-sm text-gray-700 mt-1">Pune Cantonment Board, Pune.</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleClose}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4" />
            Close
          </button>
          {canSubmit() && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
